/**
 * The context dock.
 *
 * A scrollable stack of cards, not tabs. Tabs hide things people need at the
 * same time, and the argument this product exists to support — that a clause
 * is worse than the floor beneath it — is made by holding the clause, the
 * Award and what the employer already conceded elsewhere in view together.
 *
 * Each card collapses and remembers, because the useful stack differs by
 * clause: a definitions clause is all neighbourhood, a salary clause is all
 * comparators.
 */

import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type DockData } from '../lib/api.js';

export function Dock({
  clause,
  onSelect,
}: {
  clause: string;
  onSelect: (number: string) => void;
}) {
  const dock = useQuery({ queryKey: ['dock', clause], queryFn: () => api.dock(clause) });

  if (dock.isPending) {
    return <p className="label bg-[var(--color-subtle)] p-2.5">Loading context…</p>;
  }
  if (!dock.data) return null;

  const { neighbourhood, activity, comparators } = dock.data;

  return (
    /*
      Paper cards on the subtle ground, which is what makes them cards.

      The dock was a stack of hairline-separated sections on the same paper as
      the reader beside it, so at a glance the right third of the screen read
      as more document rather than as apparatus about the document. The mockup
      grounds the dock in --color-subtle and floats paper panels on it; that
      one change is the whole difference, and it costs no colour, no shadow and
      no extra rule weight.

      `min-h-full` because the shell's aside is the scroll container and a
      short dock would otherwise leave a paper band under the last card.
    */
    <div className="flex min-h-full flex-col gap-2 bg-[var(--color-subtle)] p-2.5">
      <Card title="Neighbourhood" defaultOpen>
        <Neighbourhood data={neighbourhood} onSelect={onSelect} />
      </Card>

      {comparators.map((group) => (
        <Card key={group.kind} title={group.label} defaultOpen={group.kind === 'award'}>
          {group.note ? (
            <p className="mb-2.5 text-[11.5px] leading-snug text-[var(--color-muted)]">
              {group.note}
            </p>
          ) : null}

          {group.passages.map((passage) => (
            <figure key={`${group.kind}-${passage.number}`} className="mb-3">
              {/* The comparator's own words, in the reading register. This is
                  the one thing in the dock that gets quoted at a table, so it
                  is set as prose rather than as metadata. */}
              <blockquote className="register-reading border-l border-[var(--color-rule-strong)] pl-2.5 text-[13px] leading-relaxed">
                {passage.text}
                {passage.truncated ? '…' : ''}
              </blockquote>
              <figcaption className="label mt-1.5 leading-snug">
                {passage.instrument} · {passage.number}
                {passage.heading ? ` · ${passage.heading}` : ''}
                {passage.pageStart ? ` · p${passage.pageStart}` : ''}
              </figcaption>
            </figure>
          ))}

          {/* The honest label. There is a real difference between "the closest
              passage" and "the equivalent provision", and only the first is
              true today — quoting the second at the table on this evidence
              would be the kind of error this product exists to prevent. */}
          <p className="stamp stamp-ai">Machine-matched · not yet verified as equivalent</p>
        </Card>
      ))}

      <Card title="Activity" defaultOpen={activity.questions.length + activity.claims.length > 0}>
        <Activity data={activity} />
      </Card>
    </div>
  );
}

function Card({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="card shrink-0">
      {/*
        The disclosure is the card's header, so the whole strip is the target
        rather than a 9px word inside it. `.card-header` supplies the hairline
        and the negative inset; the chevron takes the right-hand slot the
        primitive already reserves for a stamp.

        Collapsed, the header keeps its bottom rule — a card that changes
        height on toggle should not also change what it is made of.

        No `w-full`: `.card-header` is `display: flex`, which makes the button
        a block-level box whose auto width already resolves against the card's
        content edges. Pinning it to 100% and then pulling it 12px left with
        the primitive's negative margin would leave it 24px short on the right.
      */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="hit card-header text-left"
        style={open ? undefined : { marginBottom: -12 }}
      >
        <span className="label">{title}</span>
        <span aria-hidden className="label">
          {open ? '▾' : '▸'}
        </span>
      </button>
      {open ? children : null}
    </section>
  );
}

function Neighbourhood({
  data,
  onSelect,
}: {
  data: DockData['neighbourhood'];
  onSelect: (number: string) => void;
}) {
  const broken = data.outbound.filter((r) => r.status !== 'resolved');
  const working = data.outbound.filter((r) => r.status === 'resolved');

  return (
    <>
      {data.parent ? (
        <Row label="Sits under">
          <Ref number={data.parent.number} heading={data.parent.heading} onSelect={onSelect} />
        </Row>
      ) : null}

      {data.children.length > 0 ? (
        <Row label="Contains">
          {data.children.map((child) => (
            <Ref
              key={child.number}
              number={child.number}
              heading={child.heading}
              onSelect={onSelect}
            />
          ))}
        </Row>
      ) : null}

      {broken.length > 0 ? (
        <Row label="Defective references">
          {broken.map((ref) => (
            <span key={ref.toNumber} className="block text-[12px] leading-snug">
              {/* Paired with its glyph, never colour alone — and △ is the
                  glyph the design language assigns a drafting defect. The
                  warning sign that stood here is Extended_Pictographic, so it
                  renders as a colour emoji on most platforms: the one filled,
                  pictorial mark in an interface that has none. */}
              <span aria-hidden>△ </span>
              <span className="clause-number">{ref.toNumber}</span>{' '}
              <span style={{ color: 'var(--color-muted)' }}>
                {ref.status === 'broken' ? 'points at nothing' : 'paragraph missing'}
                {ref.suggestedFix ? ` — perhaps ${ref.suggestedFix}` : ''}
              </span>
            </span>
          ))}
        </Row>
      ) : null}

      {working.length > 0 ? (
        <Row label="Refers to">
          {working.map((ref) => (
            <Ref
              key={ref.toNumber}
              number={ref.toNumber}
              heading={ref.toHeading}
              onSelect={onSelect}
            />
          ))}
        </Row>
      ) : null}

      {data.inbound.length > 0 ? (
        <Row label="Referred to by">
          {data.inbound.map((ref) => (
            <Ref key={ref.number} number={ref.number} heading={ref.heading} onSelect={onSelect} />
          ))}
        </Row>
      ) : null}
    </>
  );
}

function Activity({ data }: { data: DockData['activity'] }) {
  const empty =
    data.questions.length === 0 &&
    data.claims.length === 0 &&
    data.marks === 0 &&
    !data.leverage;

  if (empty) {
    // An empty state that generates work rather than apologising.
    return (
      <p className="text-[12px]" style={{ color: 'var(--color-muted)' }}>
        Nobody has marked this clause yet. Select any of its text to start.
      </p>
    );
  }

  return (
    <>
      {data.marks > 0 ? (
        <Row label="Marks">
          <span className="text-[12px]">
            {data.marks} on this clause
            {/* Private marks are counted only for their own author: the count
                comes back through the same policy the rows do. */}
          </span>
        </Row>
      ) : null}

      {data.questions.length > 0 ? (
        <Row label="Questions">
          {data.questions.map((q) => (
            <span key={q.id} className="block text-[12px]">
              {q.text}
              {/* The triage lane, paired with its wording rather than shown
                  as a colour: the four lanes have to be legible in a printed
                  export as well as on screen. */}
              <span className="label ml-1">{q.status.replace(/_/g, ' ')}</span>
            </span>
          ))}
        </Row>
      ) : null}

      {data.claims.length > 0 ? (
        <Row label="Claims relying on it">
          {data.claims.map((c) => (
            <span key={c.ref} className="block text-[12px]">
              <span className="clause-number">{c.ref}</span> {c.title}
              <span className="label ml-1">{c.status}</span>
            </span>
          ))}
        </Row>
      ) : null}

      {data.leverage ? (
        <Row label="Leverage">
          {data.leverage.ask ? <Lever term="Ask" value={data.leverage.ask} /> : null}
          {data.leverage.precedent ? (
            <Lever term="Precedent" value={data.leverage.precedent} />
          ) : null}
          {data.leverage.pressure ? <Lever term="Pressure" value={data.leverage.pressure} /> : null}
          {data.leverage.watch ? <Lever term="Watch" value={data.leverage.watch} /> : null}
          {data.leverage.draftedBy === 'ai' && !data.leverage.verifiedAt ? (
            <p className="stamp stamp-ai mt-1">AI drafted · unverified</p>
          ) : null}
        </Row>
      ) : null}
    </>
  );
}

function Lever({ term, value }: { term: string; value: string }) {
  return (
    <p className="mb-1 text-[12px]">
      <span className="label mr-1">{term}</span>
      {value}
    </p>
  );
}

/*
 * A named group inside a dock card.
 *
 * On `.row`, so the hairline belongs to the entry rather than to the gap and
 * the last one drops it without the card counting its children — which it
 * cannot do here anyway, since Neighbourhood and Activity both return a
 * fragment whose rows are conditional on the data.
 *
 * Stacked rather than the mockup's label-in-a-gutter: at 320px the dock has
 * about 276px of content, and a 60px gutter leaves 200px for a question
 * somebody typed in a sentence.
 */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="row flex-col items-start gap-1">
      <p className="label">{label}</p>
      <div className="w-full min-w-0">{children}</div>
    </div>
  );
}

function Ref({
  number,
  heading,
  onSelect,
}: {
  number: string;
  heading: string | null;
  onSelect: (number: string) => void;
}) {
  return (
    <button
      onClick={() => onSelect(number)}
      className="hit block text-left text-[12px]"
      title={heading ?? number}
    >
      <span className="clause-number">{number}</span>
      {heading ? <span style={{ color: 'var(--color-muted)' }}> {heading}</span> : null}
    </button>
  );
}
